import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminPurchasesListAction, adminPurchasesProvidersAction } from "../Action/action";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function moneyLabel(amount, currency) {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  const cur = currency ? String(currency).toUpperCase() : "";
  return cur ? `${cur} ${n.toFixed(2)}` : n.toFixed(2);
}

function discountRuleLabel(coupon) {
  if (!coupon || coupon.coupon_discount == null) return "—";
  const n = Number(coupon.coupon_discount);
  if (!Number.isFinite(n)) return "—";
  return String(coupon.coupon_type) === "amount" ? moneyLabel(n, coupon.currency) : `${n}%`;
}

function saleDiscountRuleLabel(sale) {
  if (!sale || sale.discount == null) return "—";
  const n = Number(sale.discount);
  if (!Number.isFinite(n)) return "—";
  return String(sale.discount_type) === "amount" ? moneyLabel(n, sale.currency) : `${n}%`;
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "badge-success";
  if (s === "pending") return "badge-warning";
  if (s === "failed" || s === "cancelled" || s === "canceled" || s === "refunded" || s === "expired") {
    return "badge-danger";
  }
  return "badge-muted";
}

export default function Purchases() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");
  const [provider, setProvider] = useState("All");
  const [onboarding, setOnboarding] = useState("All");
  const [providers, setProviders] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 10 });
  const [activePurchase, setActivePurchase] = useState(null);

  useEffect(() => {
    setPage(1);
  }, [query, status, provider, onboarding]);

  useEffect(() => {
    let cancelled = false;
    async function loadProviders() {
      try {
        const res = await adminPurchasesProvidersAction();
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data)) {
          setProviders(res.data);
        }
      } catch (e) {
        if (!cancelled) setProviders([]);
      }
    }
    loadProviders();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminPurchasesListAction({
          page,
          per_page: perPage,
          search: query.trim() || undefined,
          status: status === "All" ? undefined : status.toLowerCase(),
          payment_provider: provider === "All" ? undefined : provider,
          onboarding_status: onboarding === "All" ? undefined : onboarding.toLowerCase()
        });
        if (cancelled) return;
        if (!res?.success) {
          setRows([]);
          setMeta({ total: 0, page, per_page: perPage });
          setError(res?.message || res?.msg || "Failed to load purchases.");
          return;
        }
        setRows(Array.isArray(res.data) ? res.data : []);
        setMeta({
          total: Number(res?.meta?.total || 0),
          page: Number(res?.meta?.page || page),
          per_page: Number(res?.meta?.per_page || perPage)
        });
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setMeta({ total: 0, page, per_page: perPage });
        setError(e?.response?.data?.message || e?.message || "Failed to load purchases.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [page, perPage, query, status, provider, onboarding, reloadNonce]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setActivePurchase(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const total = meta?.total ? Number(meta.total) : 0;
  const totalPages = total ? Math.max(1, Math.ceil(total / perPage)) : 1;
  const start = total && rows.length ? (page - 1) * perPage + 1 : 0;
  const end = total && rows.length ? start + rows.length - 1 : 0;

  const pageButtons = useMemo(() => {
    const max = 5;
    const safeTotal = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, page), safeTotal);
    let s = Math.max(1, current - 2);
    let e = Math.min(safeTotal, s + max - 1);
    s = Math.max(1, e - max + 1);
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const clearAndReload = () => {
    setQuery("");
    setStatus("All");
    setProvider("All");
    setOnboarding("All");
    setPage(1);
    setError("");
    setReloadNonce((n) => n + 1);
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Purchases data unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="flex items-center gap-3" style={{ flexWrap: "wrap" }}>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search user, course, provider id…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="filter-group">
            {["All", "Completed", "Pending", "Failed"].map((v) => (
              <button
                key={v}
                className={`filter-btn ${status === v ? "active" : ""}`}
                type="button"
                onClick={() => setStatus(v)}
              >
                {v}
              </button>
            ))}
          </div>
          <select
            className="form-control w-130"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            aria-label="Filter by payment provider"
          >
            <option value="All">All providers</option>
            {providers.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select
            className="form-control w-130"
            value={onboarding}
            onChange={(e) => setOnboarding(e.target.value)}
            aria-label="Filter by onboarding status"
          >
            <option value="All">All onboarding</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
          </select>
          <button className="btn btn-outline" type="button" onClick={clearAndReload} disabled={loading}>
            ↻ Clear
          </button>
        </div>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>User</th>
                <th>Course</th>
                <th>Amount</th>
                <th>Provider</th>
                <th>Status</th>
                <th>Onboarding</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((p, index) => (
                  <tr key={p.id}>
                    <td>{index + 1}</td>
                    <td>
                      {p.user_id ? (
                        <Link to={`/users/${p.user_id}`} className="action-link">
                          <strong>{p.user_name || p.user_email || `User #${p.user_id}`}</strong>
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>{p.course_title || "—"}</td>
                    <td>{p.amount_label || "—"}</td>
                    <td>{p.payment_provider || "—"}</td>
                    <td>
                      <span className={`badge ${statusBadgeClass(p.status)}`}>{p.status || "—"}</span>
                    </td>
                    <td>{p.onboarding_status || "—"}</td>
                    <td>{formatDate(p.created_at)}</td>
                    <td>
                      <div className="actions-cell">
                        <button
                          type="button"
                          className="action-link"
                          style={{ background: "transparent", border: "none", padding: 0 }}
                          onClick={() => setActivePurchase(p)}
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9}>
                    <div className="empty-state">
                      <div className="empty-icon">🧾</div>
                      No purchases match your filters.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span>
            Showing {start}-{end} of {total} Purchases
          </span>
          <div className="pagination-pages">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1 || loading}
              title="First page"
              aria-label="First page"
            >
              {"<<"}
            </button>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
              ‹
            </button>
            {pageButtons.map((p) => (
              <button
                key={p}
                className={p === page ? "active" : ""}
                type="button"
                onClick={() => setPage(p)}
                disabled={loading}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
            >
              ›
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages || loading}
              title="Last page"
              aria-label="Last page"
            >
              {">>"}
            </button>
          </div>
        </div>
      </div>

      <div
        className={`modal-overlay ${activePurchase ? "active" : ""}`}
        onClick={() => setActivePurchase(null)}
      >
        <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Purchase #{activePurchase?.id || ""}</h3>
            <button className="modal-close" type="button" onClick={() => setActivePurchase(null)}>
              ✕
            </button>
          </div>
          <div className="modal-body">
            {activePurchase ? (
              <div className="info-grid" style={{ marginBottom: 0 }}>
                <div className="info-item">
                  <label>User</label>
                  <p>
                    {activePurchase.user_id ? (
                      <Link to={`/users/${activePurchase.user_id}`} className="action-link">
                        {activePurchase.user_name || activePurchase.user_email || `User #${activePurchase.user_id}`}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </p>
                </div>
                <div className="info-item">
                  <label>Email</label>
                  <p>{activePurchase.user_email || "—"}</p>
                </div>
                <div className="info-item">
                  <label>Phone</label>
                  <p>{activePurchase.user_phone || "—"}</p>
                </div>
                <div className="info-item">
                  <label>Course</label>
                  <p>{activePurchase.course_title || "—"}</p>
                </div>
                <div className="info-item">
                  <label>Amount</label>
                  <p>{activePurchase.amount_label || "—"}</p>
                </div>
                <div className="info-item">
                  <label>Status</label>
                  <p>
                    <span className={`badge ${statusBadgeClass(activePurchase.status)}`}>
                      {activePurchase.status || "—"}
                    </span>
                  </p>
                </div>
                <div className="info-item">
                  <label>Provider</label>
                  <p>{activePurchase.payment_provider || "—"}</p>
                </div>
                <div className="info-item">
                  <label>Provider ID</label>
                  <p style={{ wordBreak: "break-all" }}>{activePurchase.payment_provider_id || "—"}</p>
                </div>
                <div className="info-item">
                  <label>Onboarding</label>
                  <p>
                    {[activePurchase.onboarding_status, activePurchase.onboarding_step != null ? `Step ${activePurchase.onboarding_step}` : null]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </p>
                </div>
                <div className="info-item">
                  <label>Onboarding completed</label>
                  <p>{formatDate(activePurchase.onboarding_completed_at)}</p>
                </div>
                <div className="info-item">
                  <label>Purchased</label>
                  <p>{formatDate(activePurchase.created_at)}</p>
                </div>
              </div>
            ) : null}
            {activePurchase?.coupon ? (
              <div style={{ marginTop: 20 }}>
                <h4 style={{ margin: "0 0 12px", fontSize: 15 }}>Applied Coupon</h4>
                <div className="info-grid" style={{ marginBottom: 0 }}>
                  <div className="info-item">
                    <label>Code</label>
                    <p>{activePurchase.coupon.coupon_code || "—"}</p>
                  </div>
                  <div className="info-item">
                    <label>Name</label>
                    <p>{activePurchase.coupon.coupon_name || "—"}</p>
                  </div>
                  <div className="info-item">
                    <label>Rule</label>
                    <p>
                      {activePurchase.coupon.coupon_type
                        ? `${String(activePurchase.coupon.coupon_type).charAt(0).toUpperCase()}${String(
                            activePurchase.coupon.coupon_type
                          )
                            .slice(1)
                            .toLowerCase()}`
                        : "—"}{" "}
                      · {discountRuleLabel(activePurchase.coupon)}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Status</label>
                    <p>
                      <span className={`badge ${statusBadgeClass(activePurchase.coupon.status)}`}>
                        {activePurchase.coupon.status || "—"}
                      </span>
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Original Price</label>
                    <p>{moneyLabel(activePurchase.coupon.original_price, activePurchase.coupon.currency)}</p>
                  </div>
                  <div className="info-item">
                    <label>Discount Amount</label>
                    <p>{moneyLabel(activePurchase.coupon.discount_amount, activePurchase.coupon.currency)}</p>
                  </div>
                  <div className="info-item">
                    <label>Payable / Discounted</label>
                    <p>{moneyLabel(activePurchase.coupon.discounted_price, activePurchase.coupon.currency)}</p>
                  </div>
                  <div className="info-item">
                    <label>Applied On</label>
                    <p>{formatDate(activePurchase.coupon.created_on)}</p>
                  </div>
                </div>
              </div>
            ) : null}
            {activePurchase?.sale ? (
              <div style={{ marginTop: 20 }}>
                <h4 style={{ margin: "0 0 12px", fontSize: 15 }}>Applied Sale</h4>
                <div className="info-grid" style={{ marginBottom: 0 }}>
                  <div className="info-item">
                    <label>Name</label>
                    <p>{activePurchase.sale.sale_name || "—"}</p>
                  </div>
                  <div className="info-item">
                    <label>Rule</label>
                    <p>
                      {activePurchase.sale.discount_type
                        ? `${String(activePurchase.sale.discount_type).charAt(0).toUpperCase()}${String(
                            activePurchase.sale.discount_type
                          )
                            .slice(1)
                            .toLowerCase()}`
                        : "—"}{" "}
                      · {saleDiscountRuleLabel(activePurchase.sale)}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Original Price</label>
                    <p>{moneyLabel(activePurchase.sale.original_price, activePurchase.sale.currency)}</p>
                  </div>
                  <div className="info-item">
                    <label>Discount Amount</label>
                    <p>{moneyLabel(activePurchase.sale.discount_amount, activePurchase.sale.currency)}</p>
                  </div>
                  <div className="info-item">
                    <label>Payable / Discounted</label>
                    <p>{moneyLabel(activePurchase.sale.discounted_price, activePurchase.sale.currency)}</p>
                  </div>
                  <div className="info-item">
                    <label>Applied On</label>
                    <p>{formatDate(activePurchase.sale.created_on)}</p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
